import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { Lock, User, Shield, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminLoginPage() {
  const navigate = useNavigate();
  const [credentials, setCredentials] = useState({
    username: '',
    password: ''
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!credentials.username || !credentials.password) {
      toast.error('Please enter username and password');
      return;
    }

    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/admin/login`, credentials);
      
      if (response.data.success) {
        // Store admin token in session storage
        sessionStorage.setItem('adminToken', response.data.token);
        sessionStorage.setItem('adminUser', credentials.username);
        toast.success('Login successful');
        navigate('/admin/dashboard');
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="min-h-screen bg-[#050505] flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 mb-6">
            <Shield className="w-4 h-4 text-[#D4AF37]" />
            <span className="text-xs text-[#D4AF37] tracking-wider uppercase">
              Admin Portal
            </span>
          </div>
          <h1 className="text-3xl font-serif text-white mb-2">
            Capital Chauffeur Services
          </h1>
          <p className="text-[#A3A3A3]">
            Admin Dashboard Login
          </p>
        </div>

        {/* Login Form */}
        <div className="glass p-8">
          <form onSubmit={handleLogin} className="space-y-6">
            <div className="space-y-2">
              <Label className="text-white flex items-center gap-2">
                <User className="w-4 h-4 text-[#D4AF37]" />
                Username
              </Label>
              <Input
                data-testid="admin-username"
                type="text"
                value={credentials.username}
                onChange={(e) => setCredentials(prev => ({ ...prev, username: e.target.value }))}
                placeholder="Enter username"
                className="bg-transparent border-white/20 focus:border-[#D4AF37] text-white placeholder:text-white/30"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-white flex items-center gap-2">
                <Lock className="w-4 h-4 text-[#D4AF37]" />
                Password
              </Label>
              <Input
                data-testid="admin-password"
                type="password"
                value={credentials.password}
                onChange={(e) => setCredentials(prev => ({ ...prev, password: e.target.value }))}
                placeholder="Enter password"
                className="bg-transparent border-white/20 focus:border-[#D4AF37] text-white placeholder:text-white/30"
              />
            </div>

            <Button
              data-testid="admin-login-btn"
              type="submit"
              disabled={isLoading}
              className="w-full bg-[#D4AF37] text-black hover:bg-[#B5952F] rounded-none py-6 text-sm font-medium tracking-wider uppercase disabled:opacity-50"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Signing In...
                </>
              ) : (
                'Sign In'
              )}
            </Button>
          </form>

          <div className="mt-6 pt-6 border-t border-white/10 text-center">
            <p className="text-[#666] text-xs">
              Secure admin access only
            </p>
          </div>
        </div>

        {/* Back to Website */}
        <div className="text-center mt-8">
          <a 
            href="/"
            className="text-[#A3A3A3] hover:text-[#D4AF37] text-sm transition-colors"
          >
            ← Back to Website
          </a>
        </div>
      </div>
    </div>
  );
}
